import { jwtDecode } from 'jwt-decode';
import { isEqual, sortBy } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AUTH_TOKEN } from 'src/config';
import { IsSuperAdmin } from 'src/constants/permissionsList';
import { AuthContextValue, User } from 'src/types/auth';
import { Companies } from 'src/types/Companies';
import {
  getImpersonatedClientIdFromUser,
  getImpersonatedCompanyIdFromUser,
  getSessionClientId,
  isUserImpersonating,
} from '../utils/authImpersonation';
import axios from '../utils/axios';
import { getUserCompanyDetails } from '../utils/transformation';

export const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

/** How long to wait for the MVC parent JWT before showing Reload UI. */
export const AUTH_LOAD_TIMEOUT_MS = 20000;

type AuthProviderProps = {
  children: ReactNode;
};

interface CompanyFromMessage {
  companyId: number;
  companyName: string;
}

interface AuthMessagePayload {
  jwt?: string;
  companies?: CompanyFromMessage[];
  type?: string;
}

const postToParent = (payload: object | string) => {
  const data = typeof payload === 'string' ? payload : JSON.stringify(payload);
  window.parent.postMessage(data, '*');
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [tokenFromMessage, setTokenFromMessage] = useState('');
  const [companiesFromMessage, setCompaniesFromMessage] = useState<Companies>([]);
  const [isAuthTimedOut, setIsAuthTimedOut] = useState(false);
  const hasAckedAuthRef = useRef(false);

  const messageHandler = useCallback((event: MessageEvent) => {
    let { data }: { data: unknown } = event;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== 'object') {
      return;
    }

    const payload = data as AuthMessagePayload;
    if (typeof payload.jwt === 'string' && payload.jwt) {
      setTokenFromMessage(payload.jwt);
      const nextCompanies: Companies =
        payload.companies?.map((company) => ({
          companyId: company.companyId.toString(),
          companyName: company.companyName,
        })) || [];
      // A repeated message keeps the same reference, so `companies` and everything keyed on it
      // (the Scan Manager's scan counts among them) does not recompute.
      setCompaniesFromMessage((prev) => (isEqual(prev, nextCompanies) ? prev : nextCompanies));
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('message', messageHandler);
    postToParent({ type: 'REQUEST_JWT' });

    return () => window.removeEventListener('message', messageHandler);
  }, [messageHandler]);

  const [
    user,
    companies,
    token,
    loggedInCompanyId,
    loggedInCompanyName,
    companyId,
    clientId,
    loggedInUserId,
    isImpersonating,
    impersonatedCompanyId,
    impersonatedClientId,
    sessionClientId,
    isShuster,
    companyName,
    isSuperAdmin,
    siteName,
    isAuthReady,
  ] = useMemo(() => {
    const token = tokenFromMessage || AUTH_TOKEN;
    const user: User = token ? jwtDecode(token) : ({ LocaleId: '1033' } as User);

    const companies: Companies = user?.Companies?.length
      ? sortBy(getUserCompanyDetails(user), ['companyName'])
      : companiesFromMessage;
    const loggedInCompanyId = user?.LoggedInCompanyId;
    const companyName = user?.CompanyName || '';
    const companyId = user?.CompanyId;
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    const isShuster = user?.SiteID === '126';
    const isSuperAdmin = !!user?.Role?.includes(IsSuperAdmin[0]);

    const siteName = user?.SiteName;
    const clientId = user?.ClientId;
    const loggedInUserId = user?.LoggedInUserId;
    const loggedInCompanyName = user?.LoggedInCompanyName;

    const isImpersonating = isUserImpersonating(user);
    const impersonatedCompanyId = getImpersonatedCompanyIdFromUser(user);
    const impersonatedClientId = getImpersonatedClientIdFromUser(user);
    const sessionClientId = getSessionClientId(clientId);
    const isAuthReady = Boolean(token && user && Object.keys(user).length > 1);
    return [
      user,
      companies,
      token,
      loggedInCompanyId,
      loggedInCompanyName,
      companyId,
      clientId,
      loggedInUserId,
      isImpersonating,
      impersonatedCompanyId,
      impersonatedClientId,
      sessionClientId,
      isShuster,
      companyName,
      isSuperAdmin,
      siteName,
      isAuthReady,
    ];
  }, [companiesFromMessage, tokenFromMessage]);

  useEffect(() => {
    if (isAuthReady && !hasAckedAuthRef.current) {
      hasAckedAuthRef.current = true;
      postToParent({ type: 'AUTH_RECEIVED' });
      setIsAuthTimedOut(false);
    }
  }, [isAuthReady]);

  useEffect(() => {
    if (isAuthReady) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setIsAuthTimedOut(true);
    }, AUTH_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isAuthReady]);

  useEffect(() => {
    if (user && user.LoggedInUserId && typeof (window as any).cwr !== 'undefined') {
      (window as any).cwr('addSessionAttributes', {
        appUserId: user.LoggedInUserId,
        appCompanyId: user.LoggedInCompanyId,
        appCompanyName: user.LoggedInCompanyName,
      });
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        companies,
        token,
        loggedInCompanyId,
        loggedInCompanyName,
        companyId,
        clientId,
        loggedInUserId,
        isImpersonating,
        impersonatedCompanyId,
        impersonatedClientId,
        sessionClientId,
        isShuster,
        companyName,
        isSuperAdmin,
        siteName,
        isAuthReady,
        isAuthTimedOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
